import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../../components/ui/Table';
import { showErrorToast } from '../../utils/errorHandler';
import { getTeacher } from '../../modules/roster/api/roster.client';

export const TeacherDetails: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!teacherId) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await getTeacher(teacherId, {
          detail: true,
          include_students: true,
          include_sections: true,
        });
        setData(resp);
      } catch (err) {
        setError('Failed to load teacher details');
        showErrorToast(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [teacherId]);

  const assignments = data?.assignments || {};
  const sections = data?.sections || [];
  const students = data?.students || [];
  const stats = data?.stats || {};

  const classMap = useMemo(() => {
    const m = new Map<string, any>();
    (assignments.all_classes || []).forEach((c: any) => {
      if (c.id) m.set(c.id, `${c.name || '-'}${c.grade ? ` (Grade ${c.grade})` : ''}`);
    });
    return m;
  }, [assignments]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/admin/teachers')}>Back</Button>
        </div>
      </Layout>
    );
  }

  const teacher = data?.teacher || {};

  const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <div className="flex items-start gap-3">
      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}:</div>
      <div className="text-sm text-gray-900 dark:text-white break-words">{value ?? '-'}</div>
    </div>
  );

  return (
    <Layout>
      <div className="mb-4 flex justify-end">
        <Button variant="ghost" onClick={() => navigate('/admin/teachers')}>
          ← Back
        </Button>
      </div>

      <PageHeader title={`${(teacher.first_name || '').trim()} ${(teacher.last_name || '').trim()}`.trim() || '-'} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow
              label="Full Name"
              value={`${(teacher.first_name || '').trim()} ${(teacher.last_name || '').trim()}`.trim() || '-'}
            />
            <InfoRow label="Email" value={teacher.email} />
            <InfoRow label="Phone" value={teacher.phone} />
            <InfoRow label="Status" value={teacher.status} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Employment, Snapshot & Assignments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Row 1: Employee Code | Primary Classes */}
              <div className="flex items-start gap-3">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Employee Code:</div>
                <div className="text-sm text-gray-900 dark:text-white">{teacher.employment?.employee_code ?? '-'}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Primary Classes:</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(assignments.primary_classes || []).length > 0 ? (assignments.primary_classes || []).map((c: any) => (
                    <span key={c.id} className="px-2 py-1 rounded bg-gray-100 dark:bg-slate-800 text-xs">
                      {`${c.name || '-'}${c.grade ? ` (Grade ${c.grade})` : ''}${c.academic_year_key ? ` • ${c.academic_year_key}` : ''}`}
                    </span>
                  )) : <span>-</span>}
                </div>
              </div>

              {/* Row 2: Joining Date | Secondary Classes */}
              <div className="flex items-start gap-3">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Joining Date:</div>
                <div className="text-sm text-gray-900 dark:text-white">{teacher.employment?.joining_date ?? '-'}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Secondary Classes:</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(assignments.secondary_classes || []).length > 0 ? (assignments.secondary_classes || []).map((c: any) => (
                    <span key={c.id} className="px-2 py-1 rounded bg-gray-100 dark:bg-slate-800 text-xs">
                      {`${c.name || '-'}${c.grade ? ` (Grade ${c.grade})` : ''}${c.academic_year_key ? ` • ${c.academic_year_key}` : ''}`}
                    </span>
                  )) : <span>-</span>}
                </div>
              </div>

              {/* Row 3: Total Classes | Total Students */}
              <div className="flex items-start gap-3">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Classes:</div>
                <div className="text-sm text-gray-900 dark:text-white">{stats.total_classes ?? '-'}</div>
              </div>
              <div className="flex items-start gap-3">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Students:</div>
                <div className="text-sm text-gray-900 dark:text-white">{stats.total_students ?? '-'}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Sections</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Class Name</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Section Name</TableHead>
                  <TableHead>Academic Year</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(sections || []).map((row: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell>{row.class?.name || '-'}</TableCell>
                    <TableCell>{row.class?.grade || '-'}</TableCell>
                    <TableCell>{row.section?.name || row.section?.display_name || '-'}</TableCell>
                    <TableCell>{row.class?.academic_year_key || row.section?.academic_year_key || '-'}</TableCell>
                    <TableCell>{row.section?.status || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Students</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student Name</TableHead>
                  <TableHead>Admission No</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Section</TableHead>
                  <TableHead>Academic Year</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(students || []).map((item: any, idx: number) => {
                  const student = item.student || {};
                  const enrollment = item.current_enrollment || {};
                  const classLabel = classMap.get(enrollment.class_id) || '-';
                  const sectionObj = sections.find((s: any) => s.section?.id === enrollment.section_id);
                  const sectionLabel = sectionObj?.section?.name || '-';
                  return (
                    <TableRow key={idx}>
                      <TableCell>{student.display_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || '-'}</TableCell>
                      <TableCell>{student.admission_no || '-'}</TableCell>
                      <TableCell>{student.email || '-'}</TableCell>
                      <TableCell>{student.phone || '-'}</TableCell>
                      <TableCell>{classLabel}</TableCell>
                      <TableCell>{sectionLabel}</TableCell>
                      <TableCell>{enrollment.academic_year_key || '-'}</TableCell>
                      <TableCell>{student.status || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default TeacherDetails;

